import { FC, KeyboardEvent, ReactNode, useId } from 'react'
import { Box, Collapse, Stack, Typography } from '@mui/material'

import { BLUE_100, BLUE_600, GRAY_200, GRAY_400, GRAY_700, GRAY_900, SELECTED_BLUE, WHITE } from 'constants/styling/theme'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIRadio } from 'components/common/MUIRadio'
import { SELECTION_RING_SX, type SelectionStyle, useSelectionStyle } from 'components/common/SelectionStyle'

/**
 * @interface Props Input properties
 */
interface Props {
  /** Optional content to display as expandable when checkbox checked */
  expandableContent?: ReactNode
  /** Whether the checkbox is radio instead */
  isRadio?: boolean
  /** Whether the card should be highlighted */
  highlighted?: boolean
  /** OnCheck action to be called */
  onCheck?: () => void
  /** Whether any sort of checkbox/radio is displayed */
  showSelectElement?: boolean
  /** Whether the checkbox should be checked */
  checked?: boolean
  /** Disables checkbox selection and applies styling */
  disabled?: boolean
  /** Disables checkbox selection without applying styling */
  disabledSelection?: boolean
  /** Optional leading glyph, shown after the control. */
  icon?: ReactNode
  /** Header title; together with `description` it replaces free-form children. */
  title?: ReactNode
  /** Secondary line under the title. */
  description?: ReactNode
  /** Right-aligned header content, e.g. a price. */
  trailing?: ReactNode
  /** Optional children rendered in the header of the card */
  children?: ReactNode
  /** Optional content, that will be rendered as a footer of a card, displayed even when card is collapsed (used mainly for upsell marketing on disabled cards) */
  alwaysOnFooterContent?: ReactNode
  /** Names the control instead of the header text, e.g. "Use videos in the campaign" (with `onCheck`) */
  'aria-label'?: string
  /** Id of the element describing the control, e.g. a hint placed in `description` (with `onCheck`) */
  'aria-describedby'?: string
  /** Dashed border, for a card standing for something not there yet */
  isDashed?: boolean
  'data-cy'?: string
  /** `ring`: a selected card stays white and gets a blue outline instead of the blue fill. Defaults to the
   *  surrounding `SelectionStyleProvider`. */
  selectionStyle?: SelectionStyle
}

/**
 * @component
 * MUICheckCard represents a selectable card with an optional checkbox or radio button.
 *
 * With `onCheck` the control is the card's radio/checkbox, named by the title column beside it: it takes
 * focus and toggles on Enter or Space, and a click anywhere on the header toggles too. The header's text
 * and expandable content sit outside the role, so they may hold links and fields. Wrap a set of radio
 * cards in an element with `role="radiogroup"`.
 *
 * @example
 * <MUICheckCard isRadio checked={selected} onCheck={select} title="Credit card" description="Pay now" />
 */
export const MUICheckCard: FC<Props> = ({
  expandableContent,
  isRadio = false,
  highlighted = false,
  onCheck,
  checked,
  disabled = false,
  disabledSelection = false,
  showSelectElement = true,
  icon,
  title,
  description,
  trailing,
  children,
  alwaysOnFooterContent,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  isDashed = false,
  'data-cy': dataCy,
  selectionStyle,
}) => {
  const contextSelectionStyle = useSelectionStyle()
  const isRing = (selectionStyle ?? contextSelectionStyle) === 'ring'
  const isChecked = !!checked
  const isSelected = isChecked || highlighted
  const isInteractive = !!onCheck
  const isClickable = isInteractive && !disabledSelection && !disabled
  const Control = isRadio ? MUIRadio : MUICheckbox
  const labelId = useId()

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isClickable || (e.key !== 'Enter' && e.key !== ' ')) return
    e.preventDefault()
    onCheck?.()
  }

  return (
    <Stack
      data-cy={dataCy}
      sx={{
        borderRadius: '0.8rem',
        border: `1px ${isDashed ? 'dashed' : 'solid'} ${isSelected ? SELECTED_BLUE : GRAY_400}`,
        backgroundColor: isRing ? WHITE : isSelected ? BLUE_100 : 'transparent',
        boxShadow: isSelected && isRing ? SELECTION_RING_SX.boxShadow : undefined,
        opacity: disabled ? 0.6 : 1,
        transition: 'all 120ms ease',
        '&:hover': isClickable ? { borderColor: SELECTED_BLUE } : undefined,
      }}
    >

      <Stack
        direction="row"
        alignItems="center"
        gap={1.2}
        onClick={isClickable ? () => onCheck?.() : undefined}
        sx={{
          px: 1.6,
          py: 1.2,
          cursor: isClickable ? 'pointer' : disabled ? 'not-allowed' : 'inherit',
          userSelect: isClickable ? 'none' : 'auto',
        }}
      >

        {/* The role sits on the control alone and takes its name from the text beside it: descendants of a
            checkbox or radio are presentational, so links in the header must stay outside it. */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1.2}
          role={isInteractive ? (isRadio ? 'radio' : 'checkbox') : undefined}
          aria-checked={isInteractive ? isChecked : undefined}
          aria-disabled={isInteractive && !isClickable ? true : undefined}
          aria-label={isInteractive ? ariaLabel : undefined}
          aria-labelledby={isInteractive && !ariaLabel ? labelId : undefined}
          aria-describedby={isInteractive ? ariaDescribedBy : undefined}
          tabIndex={isInteractive ? (isClickable ? 0 : -1) : undefined}
          onKeyDown={handleKeyDown}
          sx={{
            flexShrink: 0,
            outline: 'none',
            borderRadius: '0.4rem',
            '&:focus-visible': { boxShadow: `0 0 0 2px ${BLUE_600}` },
          }}
        >

          {/* Cancelling the control's own click keeps it from reaching the header twice through its label. */}
          {showSelectElement &&
            <Control
              checked={isChecked}
              disabled={disabled || disabledSelection}
              tabIndex={-1}
              inputProps={{ tabIndex: -1, 'aria-hidden': true }}
              onClick={(e) => e.preventDefault()}
              sx={{ p: 0, '&:hover': { backgroundColor: 'transparent' } }}
            />
          }

          {icon &&
            <Stack sx={{ color: GRAY_700, '& svg': { fontSize: '2rem' } }}>
              {icon}
            </Stack>
          }

        </Stack>

        <Stack id={labelId} gap={0.2} sx={{ flex: 1, minWidth: 0 }}>

          {title &&
            <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
              {title}
            </Typography>
          }

          {description &&
            <Typography variant="text-xs" color={GRAY_700}>
              {description}
            </Typography>
          }

          {children}

        </Stack>

        {trailing &&
          <Stack sx={{ ml: 'auto', pl: 1, flexShrink: 0, alignSelf: 'center' }}>
            {trailing}
          </Stack>
        }

      </Stack>

      {!!expandableContent &&
        <Collapse in={isChecked} unmountOnExit>
          <Stack sx={{ px: 1.6, pb: 1.6, pt: 0.4 }}>
            {expandableContent}
          </Stack>
        </Collapse>
      }

      {!!alwaysOnFooterContent &&
        <Box mx={1.6} mb={1.6} pt={1.6} borderTop={`1px solid ${GRAY_200}`}>
          {alwaysOnFooterContent}
        </Box>
      }

    </Stack>
  )
}
